<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { sessionHelper, ddlHelper } from "@/utils";
import { SESSION, SHARED } from "@/constants";
import { required, integer, minValue } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import i18n from "@/i18n";
import { DOMAIN_MODULE } from '@/state/modules/domain'
import { DOMAIN_GROUP_MODULE } from '@/state/modules/domainGroup'
import { domainMethods, domainGroupMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";

const hostname = (value) => {
  if (value == null || value == '') {
    return true;
  }
  return /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/.test(value);
};

export default {
  page: {
    title: i18n.t("label.createX", [i18n.t("module.domain")]),
  },
  components: { Layout, PageHeader, Multiselect },
  beforeRouteLeave(to, from, next) {
    if (to.name != "Domain-listing") {
      sessionHelper.clearSearch(SESSION.DOMAIN_SEARCH);
    }
    next();
  },
  computed: {
    ...mapState(DOMAIN_MODULE, ["status"]),
    ...mapState(DOMAIN_GROUP_MODULE, { groupList: (state) => state.list.data }),
    groupDdl() {
      // A group is one brand in one market: only groups of the domain's own
      // country can take it.
      return this.groupList
        .filter((g) => !this.createForm.countryCode || g.countryCode === this.createForm.countryCode)
        .map((g) => {
          return { value: g.id, text: g.slug ? g.name + ' (' + g.slug + ')' : g.name };
        });
    },
    selectedGroup: {
      get() {
        return this.groupDdl.find((g) => g.value === this.createForm.domainGroupUuid) || null;
      },
      set(option) {
        this.createForm.domainGroupUuid = option ? option.value : null;
      },
    },
    selectedCountryCode: {
      get() {
        return this.countryCodeDdl.find((c) => c.value === this.createForm.countryCode) || null;
      },
      set(option) {
        this.createForm.countryCode = option ? option.value : null;
      },
    },
  },
  data() {
    return {
      title: i18n.t("label.createX", [i18n.t("module.domain")]),
      items: [
        {
          text: i18n.t("module.dashboard"),
          href: "/",
        },
        {
          text: i18n.t("module.domain"),
          href: "/domain",
        },
        {
          text: i18n.t("label.createX", [i18n.t("module.domain")]),
          active: true,
        },
      ],
      submitted: false,
      permissions: sessionHelper.getPermissions(),
      createPermission: null,
      // Only the markets this user may see.
      countryCodeDdl: ddlHelper.getMonitorCountries(),
      createForm: {
        domain: "",
        countryCode: sessionHelper.canSeeCountry("AU") ? "AU" : (ddlHelper.getMonitorCountries()[0] || {}).value || null,
        remark: "",
        domainGroupUuid: null,
      },
    };
  },
  validations: {
    createForm: {
      domain: { required, hostname },
      countryCode: { required },
    },
  },
  async created() {
    await this.validPermissions();
    this.loadGroups();
  },
  methods: {
    ...domainMethods,
    ...domainGroupMethods,
    loadGroups() {
      // Groups belong to the current user; the backend scopes the list.
      this.getAllDomainGroups({
        filter: {
          pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
          pageSize: 100,
          isActive: SHARED.DEFAULT_ALL_VALUE,
          searchArray: [],
        },
      });
    },
    async validPermissions() {
      var jsonPermissions = JSON.parse(this.permissions)
      if (jsonPermissions.some(p => p.code === "DOMAIN_CREATE")) {
        this.createPermission = true;
      }
      else {
        this.createPermission = false;
      }
    },
    normalizeDomain() {
      let value = (this.createForm.domain || '').trim().toLowerCase();
      value = value.replace(/^[a-z]+:\/\//, '');
      value = value.split('/')[0];
      this.createForm.domain = value;
    },
    requestCreate() {
      this.normalizeDomain();
      this.submitted = true;
      // stop here if form is invalid
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      } else {
        let data = {
          domain: this.createForm.domain,
          countryCode: this.createForm.countryCode,
          remark: this.createForm.remark,
          domainGroupUuid: this.createForm.domainGroupUuid,
        };

        if (this.createPermission == true) {
          this.createDomain({ data });
        }
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col">
        <div class="card">
          <div class="card-body">
            <form @submit.prevent="requestCreate" v-if="createPermission == true">
              <b-form-group id="domain" label-cols-sm="2" label-cols-lg="2" :label="$t('label.domain')"
                label-for="domain">
                <b-form-input for="domain" v-model.trim="createForm.domain" @change="normalizeDomain" :class="{
                  'is-invalid': submitted &&
                    (!$v.createForm.domain.required || !$v.createForm.domain.hostname)
                }"></b-form-input>
                <div v-if="submitted &&
                  (!$v.createForm.domain.required || !$v.createForm.domain.hostname)" class="invalid-feedback">
                  <span v-if="!$v.createForm.domain.required">{{
                    $t("message.requiredX", [$t("label.domain")])
                  }}</span>
                  <span v-if="!$v.createForm.domain.hostname">{{
                    $t("message.validX", [$t("label.domain")])
                  }}</span>
                </div>
              </b-form-group>

              <b-form-group id="countryCode" label-cols-sm="2" label-cols-lg="2" :label="$t('label.countryCode')"
                label-for="countryCode">
                <multiselect v-model="selectedCountryCode" :options="countryCodeDdl" label="text" track-by="value"
                  :show-labels="false" :allow-empty="false"
                  :placeholder="$t('message.pleaseSelectX', [$t('label.countryCode')])"></multiselect>
                <div v-if="submitted && !$v.createForm.countryCode.required" class="invalid-feedback">
                  <span v-if="!$v.createForm.countryCode.required">{{
                    $t("message.requiredX", [$t("label.countryCode")])
                  }}</span>
                </div>
              </b-form-group>

              <b-form-group id="group" label-cols-sm="2" label-cols-lg="2" :label="$t('label.group')"
                label-for="group">
                <multiselect v-model="selectedGroup" :options="groupDdl" label="text" track-by="value"
                  :show-labels="false" :allow-empty="true"
                  :placeholder="$t('message.pleaseSelectX', [$t('label.group')])"></multiselect>
                <small class="form-text text-muted">{{ $t('message.domainGroupHelp') }}</small>
              </b-form-group>


              <b-form-group id="remark" label-cols-sm="2" label-cols-lg="2" :label="$t('label.remark')"
                label-for="remark">
                <b-form-textarea for="remark" v-model.trim="createForm.remark" rows="3"></b-form-textarea>
              </b-form-group>

              <div class="mt-4 text-center" style="float: right">
                <button class="btn btn-info w-md waves-effect waves-light" style="margin-right: 10px" type="submit"
                  :disabled="status.processing">
                  {{ $t("label.createX", [$t("module.domain")]) }}
                </button>
              </div>
            </form>
            <div style="text-align: center;" v-else>
              {{ $t('message.noPermissionMessage') }}
            </div>
          </div>
        </div>
        <!-- end card -->
      </div>
      <!-- end col -->
    </div>
  </Layout>
</template>
